import { Typography } from 'antd';
import { omit } from 'lodash';
import { Moment } from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { User } from 'api/models';
import { TimesheetAPI } from 'api/timesheets';
import { ToastDescription, toastService } from 'components/molecules/toast';
import {
  Timesheet,
  TimesheetStatus,
  UserTimesheet,
} from 'components/organisms/timesheets/types';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { callWorkerWithCleanup } from 'utils/call-worker-with-cleanup';
import { fullName } from 'utils/full-name';

export const useTimesheetApprovals = (
  startDate: Moment,
  timesheets: Timesheet[],
  onTimesheetStateChange: (
    userId: number,
    payload: Partial<Omit<Timesheet, 'userId'>>,
  ) => void,
) => {
  const translate = useTranslate();
  const [userTimesheets, setUserTimesheets] = useState<UserTimesheet[]>([]);
  const userIds = useMemo(
    () => timesheets.map((timesheet) => timesheet.userId),
    [timesheets],
  );
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    const worker = callWorkerWithCleanup();

    if (timesheets.length > 0) {
      setLoading(true);
      worker.workerApi
        .getUsersByIds(userIds)
        .then((data) => {
          setLoading(false);
          const timesheetData = timesheets
            .map((timesheet) => {
              const user = data[timesheet.userId];

              return {
                user,
                ...omit(timesheet, 'userId'),
              };
            })
            .filter(Boolean) as UserTimesheet[];

          setUserTimesheets(timesheetData);
        })
        .catch(() => {
          setLoading(false);
        })
        .finally(() => {
          worker.cleanup();
        });
    }
  }, [timesheets, userIds]);

  const approveTimesheet = useCallback(
    (userId: number, updatedAt: number, projectId?: number) => {
      return TimesheetAPI.approveTimesheet(
        userId,
        startDate.format('YYYY-MM-DD'),
        updatedAt,
        projectId,
      ).then(({ updatedAt: lastUpdatedAt }) => {
        onTimesheetStateChange(userId, {
          status: TimesheetStatus.Approved,
        });

        toastService({
          title: translate(
            'timesheets.toast.titles.approved' as TranslationKey,
          ),
          message: (
            <ToastDescription
              message={translate(
                'timesheets.toast.messages.approved' as TranslationKey,
                {
                  b: (chunks) => (
                    <Typography.Text strong>
                      {fullName(
                        userTimesheets.find((u) => u.user.userId === userId)
                          ?.user as User,
                      ) ?? chunks}
                    </Typography.Text>
                  ),
                },
              )}
            />
          ),
        }).success();

        return { lastUpdatedAt };
      });
    },
    [onTimesheetStateChange, startDate, translate, userTimesheets],
  );

  const sendReminder = useCallback(
    (user: User, projectId?: number) => {
      return TimesheetAPI.sendReminder(
        user.userId,
        startDate.format('YYYY-MM-DD'),
        projectId,
      ).then((reminder) => {
        toastService({
          title: translate(
            'timesheets.toast.titles.reminder_sent' as TranslationKey,
          ),
          message: (
            <ToastDescription
              message={translate(
                'timesheets.toast.messages.reminder_sent' as TranslationKey,
                {
                  b: (chunks) => (
                    <Typography.Text strong>
                      {fullName(user) ?? chunks}
                    </Typography.Text>
                  ),
                },
              )}
            />
          ),
        }).success();

        return reminder;
      });
    },
    [startDate, translate],
  );

  return {
    userTimesheets,
    approveTimesheet,
    isLoading,
    sendReminder,
  };
};
